<template>
  <b-card no-body>
    <div class="m-2">
      <b-row>
        <b-col
          cols="12"
          md="3"
          class="mb-1 mb-md-0"
        >
          <label>หมวด</label>
          <b-form-select
            v-model="filters.category"
            :options="categoryOptions"
          />
        </b-col>

        <b-col
          cols="12"
          md="2"
          class="mb-1 mb-md-0"
        >
          <label>ที่มา</label>
          <b-form-select
            v-model="filters.source"
            :options="sourceOptions"
          />
        </b-col>

        <b-col
          cols="12"
          md="2"
          class="mb-1 mb-md-0"
        >
          <label>สถานะ</label>
          <b-form-select
            v-model="filters.is_active"
            :options="activeOptions"
          />
        </b-col>

        <b-col
          cols="12"
          md="5"
          class="d-flex align-items-end justify-content-end"
        >
          <b-form-input
            v-model="searchQuery"
            class="d-inline-block mr-1"
            placeholder="ค้นหาชื่อ (ทุกภาษา)"
          />
          <b-button
            variant="primary"
            :to="{ name: 'poi-create' }"
          >
            เพิ่มสถานที่
          </b-button>
        </b-col>
      </b-row>

      <!-- ⚠️ ODbL: ต้องแสดงเครดิตตราบใดที่ข้อมูลมาจาก OpenStreetMap -->
      <small class="text-muted">
        ทั้งหมด {{ totals }} แห่ง · ข้อมูลตั้งต้นจาก © OpenStreetMap contributors
      </small>
    </div>

    <b-table
      ref="refListTable"
      :items="items"
      :fields="tableColumns"
      :busy="isLoading"
      responsive
      striped
      primary-key="id"
      show-empty
      empty-text="ไม่พบข้อมูล"
      class="position-relative"
    >
      <template #cell(name)="data">
        <b-link
          :to="{ name: 'poi-edit', params: { id: data.item.id } }"
          class="font-weight-bold"
        >
          {{ displayName(data.item) }}
        </b-link>
        <div
          v-if="data.item.brand"
          class="text-muted small"
        >
          {{ data.item.brand }}
        </div>
      </template>

      <template #cell(category)="data">
        <b-badge :variant="data.item.category === 'international_school' ? 'light-primary' : 'light-secondary'">
          {{ categoryLabel[data.item.category] || data.item.category }}
        </b-badge>
      </template>

      <template #cell(coord)="data">
        <small>{{ data.item.lat }}, {{ data.item.lng }}</small>
      </template>

      <template #cell(source)="data">
        {{ data.item.source === 'osm' ? 'OSM' : 'เพิ่มเอง' }}
        <!-- ล็อกแล้ว = import จาก OSM รอบหน้าจะไม่ทับ -->
        <feather-icon
          v-if="Number(data.item.is_locked) === 1"
          icon="LockIcon"
          size="14"
          class="ml-25"
        />
      </template>

      <template #cell(is_active)="data">
        <b-badge :variant="Number(data.item.is_active) === 1 ? 'light-success' : 'light-secondary'">
          {{ Number(data.item.is_active) === 1 ? 'ใช้งาน' : 'ปิด' }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          toggle-class="text-decoration-none"
          no-caret
        >
          <template v-slot:button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="text-body align-middle mr-25"
            />
          </template>
          <b-dropdown-item :to="{ name: 'poi-edit', params: { id: data.item.id } }">
            <feather-icon
              icon="Edit2Icon"
              class="mr-50"
            />
            <span>แก้ไข</span>
          </b-dropdown-item>
          <b-dropdown-item @click="deleteConfirm(data.item)">
            <feather-icon
              icon="TrashIcon"
              class="mr-50"
            />
            <span>ลบ</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>

    <div class="mx-2 mb-2">
      <b-pagination
        v-model="currentPage"
        :total-rows="totals"
        :per-page="perPage"
        first-number
        last-number
        align="right"
      />
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormSelect, BButton, BTable, BBadge, BLink,
  BDropdown, BDropdownItem, BPagination,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import poiStoreModule from '../poiStoreModule'
import usePoiList from './usePoiList'

/**
 * รายการสถานที่ใกล้เคียง (POI)
 *
 * ต่างจากหน้ารายการอื่นในโปรเจกต์ตรงที่ไม่ใช้ b-table แบบ provider (`:items="fetchData"`)
 * เพราะต้องแสดงยอดรวมจาก API เพื่อทำเลขหน้า — provider mode คืนได้แค่แถวของหน้านั้น
 */
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormSelect,
    BButton,
    BTable,
    BBadge,
    BLink,
    BDropdown,
    BDropdownItem,
    BPagination,
  },
  setup() {
    const POI_STORE_MODULE_NAME = 'app-poi'

    if (!store.hasModule(POI_STORE_MODULE_NAME)) store.registerModule(POI_STORE_MODULE_NAME, poiStoreModule)

    onUnmounted(() => {
      if (store.hasModule(POI_STORE_MODULE_NAME)) store.unregisterModule(POI_STORE_MODULE_NAME)
    })

    return usePoiList()
  },
  methods: {
    deleteConfirm(item) {
      this.$bvModal
        .msgBoxConfirm(`ลบ "${this.displayName(item)}" ออกจากระบบ?`, {
          title: 'ยืนยันการลบ',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'ลบ',
          cancelTitle: 'ยกเลิก',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value === true) this.deleteData(item.id)
        })
    },
  },
}
</script>
